import { isMobile } from '../utils/isMobile';

import {
	IMAGE,
	VECTOR,
	BITMAP,
	TARIF_TIRAGES_UNIT,
	TARIF_TIRAGES_DEGRESSIVE_1,
	// TARIF_TIRAGES_DEGRESSIVE_2,
	DEGRESSIVE_QTY_1,
	// DEGRESSIVE_QTY_2,
} from '../constants';

const locale = 'fr';
const localeParameters = { style: 'currency', currency: 'EUR' };

export class DomWorker {
	static convertStringToHtml(str) {
		const div = document.createElement('div');
		div.innerHTML = str;
		return div.children[0];
	}

	static async getImage({
		image,
		name,
		content,
		credits,
		imageType = BITMAP,
		async = true,
		fetchPriority,
	}) {
		if (imageType !== VECTOR) {
			const imageContainer = document.createElement('picture');
			let imageRatio = 0.75;

			if (image.indexOf('entete') === 0) {
				imageRatio = innerWidth > 760 ? 0.75 : 0.9;
			} else if (['exemples', 'miniature'].some(prefix => image.indexOf(prefix) === 0)) {
				imageRatio = innerWidth > 760 ? 0.3 : 0.9;
			}

			const getSourceElement = (
				src,
				type,
				width,
				index,
				hd,
				thd,
				lastIndex
			) => {
				const source = document.createElement('source');
				source.srcset = `${src} 1x${hd ? `, ${hd} 1.5x` : ''}${
					thd ? `, ${thd} 2x` : ''
				}`;
				source.type = `image/${type}`;
				if (index !== lastIndex) {
					source.media = `(max-width: ${parseInt(width / imageRatio)}px)`;
				}
				return source;
			};

			const responsiveImage = await import(
				`images/${
					image.split('.')[0]
				}.jpg?sizes[]=450,sizes[]=700,sizes[]=1050,sizes[]=1400,sizes[]=2160`
			);
			const jpegImagesList =
				responsiveImage.images || responsiveImage.default.images;

			if (process.env.NODE_ENV === 'production') {
				const responsiveImageWebp = await import(
					`images/${
						image.split('.')[0]
					}.jpg?sizes[]=450,sizes[]=700,sizes[]=1050,sizes[]=1400,sizes[]=2160&format=webp`
				);
				const webpImagesList =
					responsiveImageWebp.images || responsiveImageWebp.default.images;

				webpImagesList.forEach(({ path, width }, index) => {
					const hd = webpImagesList[index + 1]?.path;
					const thd = webpImagesList[index + 2]?.path;
					const sourceWebP = getSourceElement(
						path,
						'webp',
						width,
						index,
						hd,
						thd,
						webpImagesList.length - 1
					);
					imageContainer.appendChild(sourceWebP);
				});
			}

			jpegImagesList.forEach(({ path, width }, index) => {
				const hd = jpegImagesList[index + 1]?.path;
				const thd = jpegImagesList[index + 2]?.path;
				const sourceJpeg = getSourceElement(
					path,
					'jpeg',
					width,
					index,
					hd,
					thd,
					jpegImagesList.length - 1
				);
				imageContainer.appendChild(sourceJpeg);
			});

			const imageElement = new Image(
				responsiveImage.width,
				responsiveImage.height
			);
			imageElement.src = jpegImagesList[jpegImagesList.length - 1].path;
			imageElement.alt = name;
			if (async) imageElement.loading = 'lazy';
			if (fetchPriority) imageElement.fetchPriority = 'high';
			imageContainer.appendChild(imageElement);

			if (responsiveImage.height > responsiveImage.width) {
				imageContainer.className = 'vertical';
			}

			if (credits) {
				const legend = document.createElement('legend');
				legend.className = 'credits';
				legend.innerHTML = credits;
				imageContainer.appendChild(legend);
			}

			return imageContainer;
		} else if (content === IMAGE && imageType === VECTOR) {
			if (typeof image === 'string' && image.endsWith('.svg')) {
				const { default: inlineSvg } = await import(`icons/${image}`);
				return DomWorker.convertStringToHtml(inlineSvg);
			}
			return DomWorker.convertStringToHtml(image);
		}
	}

	static async getEntete({
		name,
		content,
		image,
		imageType,
		title,
		imageTitle,
		subtitle,
		credits,
		link,
		links,
	}) {
		const entete = document.createElement('div');
		entete.className = 'entete';
		entete.name = name;

		const enteteContentWrapper = document.createElement(link ? 'a' : 'div');

		if (Array.isArray(links) && links.length && !link) {
			links.forEach(async (link) => {
				const linkElement = await DomWorker.getLink({
					...link,
					fetchPriority: true,
				});
				enteteContentWrapper.appendChild(linkElement);
			});
			enteteContentWrapper.classList.add('links');
		}

		if (link) {
			enteteContentWrapper.href = link;
			enteteContentWrapper.name = name;
		}

		if (title) {
			const aside = document.createElement('aside');
			aside.innerHTML = `<h2>${title}</h2>`;

			if (imageTitle) {
				let imageTitleElement;
				if (typeof imageTitle === 'string' && imageTitle.endsWith('.svg')) {
					const { default: inlineSvg } = await import(
						`images/${imageTitle.replace('.svg', '')}.svg`
					);
					imageTitleElement = DomWorker.convertStringToHtml(inlineSvg);
				} else {
					imageTitleElement = DomWorker.convertStringToHtml(imageTitle);
				}
				aside.querySelector('h2').style.display = 'none';
				aside.appendChild(imageTitleElement);
			}

			if (subtitle) {
				const aside_subtitle = document.createElement('h6');
				aside_subtitle.innerHTML = subtitle;
				aside.appendChild(aside_subtitle);
			}

			enteteContentWrapper.appendChild(aside);
		}

		if (content === IMAGE) {
			const enteteImage = await DomWorker.getImage({
				image,
				imageType,
				name,
				credits,
				async: false,
				fetchPriority: true,
			});
			enteteContentWrapper.appendChild(enteteImage);
		}

		entete.appendChild(enteteContentWrapper);

		return entete;
	}

	static getHxTitle({ value, titleLevel }) {
		const title = document.createElement(`h${titleLevel}`);
		title.innerHTML = value;
		return title;
	}

	static getParagraph({ value }) {
		const paragraph = document.createElement('p');
		paragraph.innerHTML = value;
		return paragraph;
	}

	static async getLink({
		value,
		link,
		content,
		image,
		imageType,
		fetchPriority,
		name,
		blank,
		linkStyle,
		imageAsync = true,
	}) {
		const linkElement = document.createElement('a');
		if (link) linkElement.href = link;
		if (name)
			content !== IMAGE
				? (linkElement.name = name)
				: linkElement.setAttribute('aria-label', name);
		if (blank) {
			linkElement.target = '_blank';
			linkElement.rel = 'noreferrer';
		}

		if (linkStyle) linkElement.classList.add(linkStyle);

		if (content === IMAGE) {
			const linkImage = await DomWorker.getImage({
				image,
				imageType,
				name,
				content,
				async: imageAsync,
				fetchPriority,
			});
			linkElement.appendChild(linkImage);
		}

		if (value || name) {
			const span = document.createElement('span');
			span.innerHTML = value || name;
			linkElement.appendChild(span);
		}

		return linkElement;
	}

	static getBigSquareButton({ value, buttonType, event }) {
		const button = document.createElement('button');
		button.className = 'big_square_button';
		if (buttonType) button.classList.add(buttonType);
		button.value = value;
		button.innerHTML = `<span>${value}</span>`;
		if (event) DomWorker.setEvent(button, event);
		return button;
	}

	static async getArticle({
		category,
		date,
		title,
		short_description,
		miniature: image,
		textLink,
		link,
		event,
		credits,
		blank,
		visible = true,
	}) {
		const article = document.createElement('article');
		article.innerHTML = `
		<h5>${date ? `${date} ___ ` : ''}${category ? category : ''}</h5>
		${title ? `<h4>${title}</h4>` : ''}
		${short_description ? `<p>${short_description}</p>` : ''}
		${
			link
				? `<a href="${link}" ${
						blank ? 'target="_blank" rel="noreferrer"' : ''
				  }>${textLink}</a>`
				: ''
		}`;

		if (!visible) article.style.display = 'none';

		const imageElement = await DomWorker.getImage({
			image,
			name: title,
			credits,
		});
		article.insertAdjacentElement('afterbegin', imageElement);

		if (event) DomWorker.setEvent(article, event);
		return article;
	}

	static getLegend({ value }) {
		const legend = document.createElement('legend');
		legend.innerHTML = value;
		return legend;
	}

	static async getFieldsList(fields) {
		const { getElementsFromContent } = await import('./MainChildren');
		return Promise.all(
			fields.map((field) => getElementsFromContent(field))
		).then((fields) => fields);
	}

	static async getForm({ id, fields, method, action, event }) {
		const form = document.createElement('form');
		form.id = id;
		form.method = method;
		form.action = action;

		const formFieldsList = await DomWorker.getFieldsList(fields);
		form.append(...formFieldsList);

		if (event) DomWorker.setEvent(form, event);
		return form;
	}

	static async getFieldset({ fields }) {
		const fieldset = document.createElement('fieldset');
		const formFieldsList = await DomWorker.getFieldsList(fields);
		fieldset.append(...formFieldsList);
		return fieldset;
	}

	static getInputText({ placeholder, id, name, required, defaultValue = '' }) {
		const inputText = document.createElement('input');
		inputText.type = 'text';
		inputText.id = id;
		inputText.placeholder = placeholder;
		inputText.name = name;
		inputText.value = defaultValue;
		if (required) inputText.required = 'true';
		return inputText;
	}

	static getInputMail({ placeholder, id, name, required, defaultValue = '' }) {
		const inputMail = document.createElement('input');
		inputMail.type = 'email';
		inputMail.id = id;
		inputMail.placeholder = placeholder;
		inputMail.name = name;
		inputMail.value = defaultValue;
		if (required) inputMail.required = 'true';
		return inputMail;
	}

	static getInputTel({ placeholder, id, name, required, defaultValue = '' }) {
		const inputTel = document.createElement('input');
		inputTel.type = 'tel';
		inputTel.id = id;
		inputTel.placeholder = placeholder;
		inputTel.name = name;
		inputTel.value = defaultValue;
		if (required) inputTel.required = 'true';

		return inputTel;
	}

	static getInputSubmit({ id, text: value }) {
		const inputSubmit = document.createElement('input');
		inputSubmit.type = 'submit';
		inputSubmit.id = id;
		inputSubmit.value = value;
		return inputSubmit;
	}

	static getTextarea({ placeholder, id, name, required, rows }) {
		const textarea = document.createElement('textarea');
		textarea.id = id;
		textarea.placeholder = placeholder;
		textarea.name = name;
		textarea.rows = rows;
		if (required) textarea.required = 'true';
		return textarea;
	}

	static getLabel({ text, fieldId }) {
		const labelElement = document.createElement('label');
		labelElement.innerHTML = text;
		labelElement.setAttribute('for', fieldId);
		return labelElement;
	}

	static getCheckbox({ name, checked, onChange, defaultValue = false }) {
		const checkbox = document.createElement('input');
		checkbox.type = 'checkbox';
		checkbox.checked = checked;
		checkbox.name = name;
		checkbox.id = name;
		checkbox.value = defaultValue;
		if (onChange) checkbox.addEventListener('change', onChange);
		return checkbox;
	}

	static getRadioInput({ options, name }) {
		const radioInputContainer = document.createElement('div');
		radioInputContainer.classList.add('radio-input-container');
		for (const { label, value, id, onSelect } of options) {
			const radioInput = document.createElement('input');
			radioInput.type = 'radio';
			radioInput.id = id;
			radioInput.value = value;
			radioInput.name = name;
			radioInput.addEventListener('change', onSelect);

			const inputLabel = DomWorker.getLabel({ text: label, fieldId: id });

			radioInputContainer.append(radioInput, inputLabel);
		}
		return radioInputContainer;
	}

	static getInputNumber({ defaultValue, min, max, label, name }) {
		const forceValues = ({ target }) => {
			if (target.value === '') return;

			const currentValue = parseInt(target.value);
			if (currentValue < parseInt(target.min)) {
				target.value = target.min;
			}
			if (currentValue > parseInt(target.max)) {
				target.value = target.max;
			}
		};

		const resetToMin = ({ target }) => {
			if (target.value === '') target.value = target.min;
		};

		const inputNumberContainer = document.createElement('div');
		inputNumberContainer.classList.add('fieldset');

		const labelElement = DomWorker.getLabel({ text: label, fieldId: name });

		const inputNumber = document.createElement('input');
		inputNumber.type = 'number';
		inputNumber.min = min;
		inputNumber.max = max;
		inputNumber.value = defaultValue;
		inputNumber.name = name;
		inputNumber.id = name;
		inputNumber.addEventListener('keyup', forceValues);
		inputNumber.addEventListener('blur', resetToMin);

		inputNumberContainer.append(labelElement, inputNumber);

		return inputNumberContainer;
	}

	static getIframe({ url, frameborder, title, allow }) {
		const iframe = document.createElement('iframe');
		iframe.src = url;
		iframe.title = title;
		iframe.frameborder = frameborder;
		if (typeof allow === 'string') iframe.allow = allow;
		return iframe;
	}

	static setEvent(element, { eventType, eventParams, callBack }) {
		element.addEventListener(eventType, callBack, eventParams);
		return element;
	}

	static setTitle({ value }) {
		const titleTag = document.head.querySelector('title');
		titleTag.textContent = value;
		return titleTag;
	}

	static setKeywords({ value }) {
		const metaKeywords = document.head.querySelector('meta[name="keyword"]');
		metaKeywords.content = value;
		return metaKeywords;
	}

	static setDescription({ value }) {
		const descriptionTag = document.head.querySelector(
			'meta[name="description"]'
		);
		descriptionTag.content = value;
		return descriptionTag;
	}

	static getTarifs({ title, short_description, listeArticles }) {
		const tarifsContainer = document.createElement('div');
		tarifsContainer.className = 'grilleTarifs';

		if (title) {
			const titleTarifs = document.createElement('h3');
			titleTarifs.innerHTML = title;
			tarifsContainer.appendChild(titleTarifs);
		}

		if (short_description) {
			const shortTarifs = document.createElement('h4');
			shortTarifs.innerHTML = short_description;
			tarifsContainer.appendChild(shortTarifs);
		}

		const listeTarifs = document.createElement('ul');
		listeTarifs.innerHTML = listeArticles
			.map(({ title, prices = [], descriptions = [], lineBreak = false }) => {
				return `
        <li class="${descriptions.length ? 'tarifsDescription' : 'tarifItem'}${
					lineBreak ? ' line_break' : ''
				}">
          ${title ? `<span>${title}</span>` : ''}
          ${prices
						.map(
							(price) =>
								`<span>${
									typeof price === 'number'
										? price.toLocaleString(locale, localeParameters)
										: price
								}</span>`
						)
						.join('')}
					${descriptions.map((desc) => `<span>${desc}</span>`).join('')}
        </li>`;
			})
			.join('');
		tarifsContainer.appendChild(listeTarifs);

		return tarifsContainer;
	}

	static async getColumn({ side, content }) {
		const { getElementsFromContent } = await import('./MainChildren');
		const columnElement = document.createElement('div');
		columnElement.classList.add('column', `column-${side}`);

		const renderColumnItem = (items) => {
			items.forEach((item) => {
				Array.isArray(item)
					? renderColumnItem(item)
					: columnElement.appendChild(item);
			});
		};

		await Promise.all(content.map((item) => getElementsFromContent(item))).then(
			renderColumnItem
		);

		return columnElement;
	}

	static async getSlider({ images }) {
		const sliderImages = await Promise.all(
			images.map((image) => DomWorker.getImage(image))
		);

		const cloneSliderImages = (image) => image.cloneNode(true);

		const slider = document.createElement('div');
		slider.classList.add('slider');

		const thumbnails = document.createElement('div');
		thumbnails.classList.add('thumbnails');
		thumbnails.append(...sliderImages);

		const mainImage = document.createElement('div');
		mainImage.classList.add('main');
		mainImage.append(...sliderImages.map(cloneSliderImages));

		const onThumbnailClick = ({ target }) => {
			thumbnails.querySelector('.active').classList.remove('active');
			target.classList.add('active');
		};

		thumbnails.children[0].classList.add('active');

		for (const thumbnail of thumbnails.children) {
			DomWorker.setEvent(thumbnail, {
				eventType: 'click',
				callBack: onThumbnailClick,
			});
		}

		slider.append(thumbnails, mainImage);
		return slider;
	}

	static getFileUploader({
		defaultInputText,
		uploadMoreInputText,
		checkAgainText,
		recapText,
		fileTypes,
	}) {
		const allFiles = [];
		window.getAllFiles = () => allFiles;

		const label = DomWorker.getLabel({
			text: defaultInputText,
			fieldId: 'file_upload',
		});

		label.classList.add('button_upload');

		const input = document.createElement('input');
		input.type = 'file';
		input.accept = fileTypes;
		input.multiple = true;
		input.name = 'file_upload';
		input.id = 'file_upload';

		// Mobile only
		const recapSelection = document.createElement('p');
		recapSelection.dataset.originalText = recapText;
		recapSelection.classList.add('recap');

		const checkSelectionAgainButton = document.createElement('button');
		checkSelectionAgainButton.innerHTML = checkAgainText;
		checkSelectionAgainButton.classList.add('button_validation');
		checkSelectionAgainButton.addEventListener('click', (event) => {
			event.preventDefault();
			const filesManager = document.querySelector('.files-manager');
			if (!filesManager) return;
			filesManager.parentElement.classList.add('open');
		});
		// End - Mobile only

		const onFileUpload = (event) => {
			const newFiles = [...event.target.files];
			const nbFiles = newFiles.length;
			const notAlreadyUploadedFiles = newFiles.filter((file) => {
				const existingFile = allFiles.find(({ name, lastModified, size }) => {
					return (
						name === file.name &&
						lastModified === file.lastModified &&
						size === file.size
					);
				});
				return !existingFile;
			});
			allFiles.push(...notAlreadyUploadedFiles);

			if (nbFiles) {
				label.innerHTML = uploadMoreInputText;
				recapSelection.innerHTML = recapSelection.dataset.originalText.replace(
					'[QTY]',
					`${allFiles.length}`
				);
			}

			DomWorker.setFilesManager({
				newFiles: notAlreadyUploadedFiles,
				allFiles,
			});
		};

		input.addEventListener('change', onFileUpload, false);

		window.addEventListener(
			'removeAllFiles',
			() => (label.innerHTML = defaultInputText)
		);

		const fieldset = document.createElement('fieldset');
		fieldset.classList.add('fileUploader', 'noFiles');
		fieldset.append(label, input, checkSelectionAgainButton, recapSelection);
		return fieldset;
	}

	static getFilesManager({
		imagesQtyText,
		estimatedPriceText,
		addMoreFilesText,
		quantitySelectorText,
		deleteImageText,
		deleteAllText,
		updateAllQtyText,
		mobileValidationSelection,
	}) {
		const filesManager = document.createElement('div');
		filesManager.classList.add('files-manager');

		const filesList = document.createElement('ul');
		filesList.dataset.quantitySelectorText = quantitySelectorText;
		filesList.dataset.deleteImageText = deleteImageText;
		filesManager.append(filesList);

		const globalFilesEditors = document.createElement('div');
		globalFilesEditors.classList.add('files-editors');

		const addMoreFilesButton = document.createElement('button');
		addMoreFilesButton.classList.add('button_upload');
		addMoreFilesButton.textContent = addMoreFilesText;
		addMoreFilesButton.addEventListener('click', () => {
			const inputFileUploader = document.querySelector(
				'.fileUploader input[type="file"]'
			);
			inputFileUploader?.click();
		});

		const deleteAllFilesButton = document.createElement('button');
		deleteAllFilesButton.classList.add('button_delete');
		deleteAllFilesButton.textContent = deleteAllText;
		deleteAllFilesButton.addEventListener('click', () => {
			const removeAllFilesEvent = new CustomEvent('removeAllFiles');
			window.dispatchEvent(removeAllFilesEvent);
		});

		const globalQtyEditor = DomWorker.getInputNumber({
			defaultValue: 1,
			min: 1,
			max: 100,
			label: updateAllQtyText,
			name: 'update_all_quantity',
		});
		globalQtyEditor.addEventListener('change', ({ target }) => {
			for (const child of filesList.children) {
				const inputQty = child.querySelector('input[type="number"]');
				inputQty.value = target.value;
				inputQty.dispatchEvent(new Event('change', { bubbles: true }));
			}
		});

		globalFilesEditors.append(
			addMoreFilesButton,
			deleteAllFilesButton,
			globalQtyEditor
		);

		const imagesNumberElement = document.createElement('p');
		imagesNumberElement.classList.add('images-qty');
		imagesNumberElement.dataset.originalText = imagesQtyText;

		const estimatedPriceElement = document.createElement('p');
		estimatedPriceElement.classList.add('price-estimation');
		estimatedPriceElement.dataset.originalText = estimatedPriceText;

		const mobileValidationButton = document.createElement('button');
		mobileValidationButton.classList.add('button_validation');
		mobileValidationButton.innerHTML = mobileValidationSelection;
		mobileValidationButton.addEventListener('click', () => {
			filesManager.parentElement.classList.remove('open');
		});

		filesManager.append(
			globalFilesEditors,
			imagesNumberElement,
			estimatedPriceElement,
			mobileValidationButton
		);
		return filesManager;
	}

	static getFileElement(
		file,
		quantitySelectorText,
		deleteImageText,
		onQtyChange
	) {
		const fileElement = document.createElement('li');
		fileElement.dataset.id = `${file.lastModified}_${file.size}`;

		const img = document.createElement('img');
		img.src = URL.createObjectURL(file);
		img.onload = () => URL.revokeObjectURL(img.src);

		const deleteImageButton = document.createElement('button');
		deleteImageButton.classList.add('button_delete');
		deleteImageButton.textContent = deleteImageText;

		const imageName = document.createElement('p');
		imageName.textContent = file.name;

		const imageQty = DomWorker.getInputNumber({
			defaultValue: 1,
			min: 1,
			max: 100,
			label: quantitySelectorText,
			name: `${file.lastModified}_${file.size}_qty`,
		});

		imageQty.addEventListener('change', onQtyChange);

		fileElement.append(img, imageName, imageQty, deleteImageButton);
		return fileElement;
	}

	static setFilesManager({ newFiles, allFiles }) {
		if (!newFiles.length) return;
		const filesManager = document.querySelector('.files-manager');
		if (!filesManager) return;

		const filesList = document.querySelector('.files-manager > ul');
		if (!filesList) return;

		document.querySelector('.fileUploader')?.classList?.remove('noFiles');

		if (isMobile()) filesManager.parentElement.classList.add('open');

		const setVariablesTexts = () => {
			const allQtyInputs = document.querySelectorAll(
				'.files-manager li input[type="number"]'
			);
			const orderQty = [...allQtyInputs].reduce(
				(globalQty, currentInput) => (globalQty += +currentInput.value),
				0
			);

			const imagesNumberElement = document.querySelector('.images-qty');
			imagesNumberElement.innerHTML =
				imagesNumberElement.dataset.originalText.replace(
					'[QTY]',
					`${orderQty}`
				);
			imagesNumberElement.dataset.exemplaires = orderQty;
			const recapSelection = document.querySelector('.recap');
			recapSelection.innerHTML = recapSelection.dataset.originalText.replace(
				'[QTY]',
				`${orderQty}`
			);

			const estimatedPriceElement = document.querySelector('.price-estimation');
			const currentUnitPrice =
				/* orderQty >= DEGRESSIVE_QTY_2
					? TARIF_TIRAGES_DEGRESSIVE_2
					: */ orderQty >= DEGRESSIVE_QTY_1
					? TARIF_TIRAGES_DEGRESSIVE_1
					: TARIF_TIRAGES_UNIT;

			// Compare to timestamp for October 1st 2025 at midnight
			const isPromoDate = new Date().getTime() < 1759269600000;
			const estimatedPrice = (orderQty * currentUnitPrice).toLocaleString(
				locale,
				localeParameters
			);
			const estimatedPromoPrice = (
				orderQty *
				currentUnitPrice *
				0.9
			).toLocaleString(locale, localeParameters);
			const displayPrice = isPromoDate
				? `<strike>${estimatedPrice}</strike> ${estimatedPromoPrice}`
				: estimatedPrice;
			estimatedPriceElement.innerHTML =
				estimatedPriceElement.dataset.originalText.replace(
					'[PRICE]',
					displayPrice
				);
		};

		for (const file of newFiles) {
			const fileElement = DomWorker.getFileElement(
				file,
				filesList.dataset.quantitySelectorText,
				filesList.dataset.deleteImageText,
				setVariablesTexts
			);

			fileElement
				.querySelector('.button_delete')
				.addEventListener('click', () => {
					const [lastModified, size] = fileElement.dataset.id.split('_');
					const fileName = fileElement.querySelector('p').textContent;

					const index = allFiles.findIndex(
						(file) =>
							file.lastModified === Number(lastModified) &&
							file.name === fileName &&
							file.size === Number(size)
					);

					console.log('delete image', { fileName, lastModified, size, index });

					allFiles.splice(index, 1);
					fileElement.remove();
					setVariablesTexts();
				});

			filesList.prepend(fileElement);
		}

		setVariablesTexts();

		const resetVariablesTexts = () => {
			const imagesNumberElement = document.querySelector('.images-qty');
			imagesNumberElement.innerHTML = '';

			const recapSelection = document.querySelector('.recap');
			recapSelection.innerHTML = '';

			const estimatedPriceElement = document.querySelector('.price-estimation');
			estimatedPriceElement.innerHTML = '';
		};

		const onRemoveAllFiles = () => {
			allFiles.length = 0;
			filesList.innerHTML = '';
			document.querySelector('.fileUploader')?.classList?.add('noFiles');
			resetVariablesTexts();
		};

		window.addEventListener('removeAllFiles', onRemoveAllFiles);
	}

	static getList({ value }) {
		const list = document.createElement('ul');
		list.className = 'list-item';
		list.append(
			...value.map((textItem) => {
				const listItem = document.createElement('li');
				listItem.innerHTML = textItem;
				return listItem;
			})
		);
		return list;
	}

	static setDialog({
		dialogId,
		initialText = '',
		initialButtonText = '',
		secondaryText = '',
		secondaryButtonText = '',
	}) {
		const dialogElement = document.createElement('dialog');
		dialogElement.id = dialogId;
		if (initialText.length) {
			const initialTextElement = document.createElement('p');
			initialTextElement.className = 'initialText';
			initialTextElement.dataset.initialText = initialText;
			initialTextElement.textContent = initialText;
			dialogElement.appendChild(initialTextElement);
		}
		if (initialButtonText.length) {
			const initialButton = document.createElement('button');
			initialButton.className = 'initialButton button_validation';
			initialButton.textContent = initialButtonText;
			dialogElement.appendChild(initialButton);
		}
		if (secondaryText.length) {
			const secondaryTextElement = document.createElement('p');
			secondaryTextElement.className = 'secondaryText';
			secondaryTextElement.textContent = secondaryText;
			dialogElement.appendChild(secondaryTextElement);
		}
		if (secondaryButtonText.length) {
			const secondaryButton = document.createElement('button');
			secondaryButton.className = 'secondaryButton button_validation';
			secondaryButton.textContent = secondaryButtonText;
			dialogElement.appendChild(secondaryButton);
		}
		document.body.appendChild(dialogElement);
	}

	static getWarning({ display, message, theme = 'dark' }) {
		if (!display) return;

		const className = 'warning-container';

		const warningElement = document.createElement('div');
		warningElement.className = `${className} ${className}-${theme}`;

		const warningMessage = document.createElement('p');
		warningMessage.textContent = message;

		warningElement.append(warningMessage);

		return warningElement;
	}
}
